import React from 'react';
import { resolveImageSrc, DEFAULT_AVATAR, avatarFallbackUrl } from '../utils/media';
import { DEFAULT_COVER } from '@/constants/media';
import { REMOTE_FALLBACK_COVER_URL } from '../constants/images';
import { Phone, Mail, Globe, Share2, Radio, ArrowRight, Linkedin, Twitter, Instagram, Facebook, Music2, QrCode as QrCodeIcon, UserPlus } from 'lucide-react';
const QrCode = QrCodeIcon;
import UnifiedBrandIcon from './UnifiedBrandIcon';
import { User, AdConfig } from '../types';
import { QRCodeModal } from './QRCodeModal';
import { EnhancedEmailLeadForm } from './EnhancedEmailLeadForm';
import { BroadcastModal } from './BroadcastModal';
import BroadcastLoginModal from './BroadcastLoginModal';
import safeToast from '../utils/safeToast';
import { resolveUserColors } from '../utils/colors';
import { mockAds } from '../data/mockData';
import { trackAdView, trackAdClick, adRedirectUrl } from '../api/analytics';
import { listLeadsByOwner } from '../api/leads';
import { canonicalCardUrl } from '@/utils/url';
import { useShare } from '@/context/ShareProvider';
import { buildSharePayload } from '@/utils/share';

interface ECardTemplate3Props {
  user: User;
  ads: AdConfig[];
  onAnalyticsUpdate?: (userId: string, metric: string) => void;
  isOwnerView?: boolean;
  onLeadSubmit?: (leadData: any) => void;
  leads?: any[];
  onContactExchange?: () => void;
}

export const ECardTemplate3: React.FC<ECardTemplate3Props> = ({ user, ads, onAnalyticsUpdate, isOwnerView = false, onLeadSubmit, leads = [], onContactExchange }) => {
  const [showQRModal, setShowQRModal] = React.useState(false);
  const [showEmailForm, setShowEmailForm] = React.useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = React.useState(false);
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const [authedLeads, setAuthedLeads] = React.useState<any[] | null>(null);
  const [currentAdIndex, setCurrentAdIndex] = React.useState(0);
  const { openShare } = useShare();

  // Check if broadcast should be enabled
  const _exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
  const _notExpired = !_exp || _exp > new Date();
  const _subActive = (user.subscription?.isActive ?? true) && _notExpired;
  const isBroadcastEnabled = isOwnerView && _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
  const isBroadcastOwnerEnabled = isBroadcastEnabled;
  const userColors = resolveUserColors(user.colors as any);
  const sourceAds = React.useMemo(() => {
    const base = Array.isArray(ads) ? [...ads] : [];
    if (base.length === 0) return mockAds;
    if (base.length >= 3) return base;
    const out = [...base];
    try {
      const mocks = (mockAds as any[]).filter((m:any) => m && m.isActive !== false);
      for (const m of mocks) {
        if (out.length >= 3) break;
        const dupe = out.find((x:any) =>
          String(x?.title || '').toLowerCase() === String(m?.title || '').toLowerCase() ||
          String(x?.link || '') === String(m?.link || '')
        );
        if (!dupe) out.push(m as any);
      }
    } catch {}
    return out;
  }, [ads]);
  const activeAds = sourceAds.filter(ad => ad.isActive);
  const currentAd = activeAds.length > 0 ? activeAds[currentAdIndex] : null;
  const currentAdHref = React.useMemo(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      const href = (currentAd as any)?.link || (currentAd as any)?.url || (currentAd as any)?.href;
      if (id && href) return adRedirectUrl(String(id), String(href));
      return String(href || '#');
    } catch { return '#'; }
  }, [currentAd, currentAdIndex]);

  // Auto-scroll ads every 5 seconds
  React.useEffect(() => {
    if (activeAds.length > 1) {
      const interval = setInterval(() => {
        setCurrentAdIndex((prevIndex) => (prevIndex + 1) % activeAds.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [activeAds.length]);

  // Track ad view whenever the visible ad changes (skip mocks without ids)
  React.useEffect(() => {
    try {
      const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId;
      if (id) {
        try { trackAdView(String(id)).catch(() => {}); } catch {}
      }
    } catch {}
  }, [currentAdIndex]);

  const handleCall = () => {
    window.open(`tel:${user.phone}`);
    // Track specific call clicks for separate reporting
    onAnalyticsUpdate?.(user.id, 'callClicks');
  };
  
  const handleEmail = () => {
    setShowEmailForm(true);
    onAnalyticsUpdate?.(user.id, 'messageClicks');
  };
  
  const handleWebsite = () => {
    window.open(user.website, '_blank');
    onAnalyticsUpdate?.(user.id, 'contactClicks');
  };
  
  const handleQR = () => {
    setShowQRModal(true);
    onAnalyticsUpdate?.(user.id, 'qrScans');
  };
  
  const handleShare = () => {
    const handle = (user as any)?.handle || (user as any)?.username || '';
    const username = (user as any)?.username || (user as any)?.id || '';
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : '';
    const cardUrl = (handle && username) ? canonicalCardUrl(handle, username) : (user.website || fallbackUrl);
    const payload = buildSharePayload({ name: user.name, url: cardUrl });
    const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|webOS/i.test(navigator.userAgent||'');
    if ((navigator as any)?.share && isMobile) {
      (navigator as any).share(payload).catch(()=> openShare(cardUrl, payload.text));
    } else {
      openShare(cardUrl, payload.text);
    }
    onAnalyticsUpdate?.(user.id, 'shares');
  };
  
  const handleBroadcast = () => {
    const eligible = _subActive && (user.subscription?.type === 'gold' || user.subscription?.type === 'platinum');
    if (!eligible) {
      try { safeToast('info', 'This function only available for Gold or Platinum User, Subscribe Now !'); } catch {}
      return;
    }
    if (isBroadcastOwnerEnabled) { setShowBroadcastModal(true); return; }
    setShowLoginModal(true);
  };
  
  const handleSaveCard = () => {
    // Create vCard data
    const vCardData = `BEGIN:VCARD
VERSION:3.0
FN:${user.name}
ORG:${user.company}
TITLE:${user.position}
TEL:${user.phone}
EMAIL:${user.email}
URL:${user.website}
NOTE:${user.bio}
END:VCARD`;

    // Create blob and download
    const blob = new Blob([vCardData], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
  link.download = `${(user.name || '').replace(/\s+/g, '-')}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    
    onAnalyticsUpdate?.(user.id, 'saves');
  };
  
  const handleSocialClick = (platform: string) => {
    onAnalyticsUpdate?.(user.id, 'socialClicks');
  };

  return (
    <>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-gray-100">
      {/* Section 1: Cover Image, Profile Pic, Name */}
      <div className="relative">
        <div className="h-[200px] bg-gray-50 relative overflow-hidden">
            {(() => {
              const customCover = Boolean((user as any).coverImage || (user as any).coverUrl);
              const coverSrc = (user as any).coverImage || (user as any).coverUrl || DEFAULT_COVER;
              const imgClass = `w-full h-full object-cover ${customCover ? 'opacity-60' : ''}`.trim();
              return (
                <img 
                  src={coverSrc}
                  alt="Cover" 
                  className={imgClass}
                  style={{ objectPosition: 'center 15%' }}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    const def = String(DEFAULT_COVER);
                    const remote = String(REMOTE_FALLBACK_COVER_URL);
                    if (img.src !== def) {
                      img.src = def;
                      e.currentTarget.className = `w-full h-full object-cover`;
                    } else if (img.src !== remote) {
                      img.src = remote;
                      e.currentTarget.className = `w-full h-full object-cover`;
                    }
                  }}
                />
              );
            })()}
          {/* Strip Line: visible below cover */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-1"
            style={{ backgroundColor: userColors.stripLine }}
          ></div>
        </div>
        
  <div className="px-6 py-8">
          <div className="flex items-center space-x-4">
            <img 
              src={user?.avatar ? resolveImageSrc(user.avatar) : avatarFallbackUrl(user?.name, (user as any)?.username)} 
              alt={user.name}
              className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
              onError={(e) => { const img = e.currentTarget as HTMLImageElement; const fb = avatarFallbackUrl(user?.name, (user as any)?.username); if (img.src !== fb) img.src = fb; }}
            />
            <div className="flex-1">
              <h1 
                className="text-xl font-bold leading-tight"
                style={{ color: userColors.text }}
              >
                {user.name}
              </h1>
              <p 
                className="text-sm font-medium"
                style={{ color: userColors.primary }}
              >
                {user.position}
              </p>
              <p 
                className="text-xs"
                style={{ color: userColors.secondary }}
              >
                {user.company}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6">
        {/* Section 2: Action Buttons */}
        <div className="mb-6">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button 
              onClick={handleCall}
              className="flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all"
              style={{ 
                borderColor: `${userColors.primary}30`,
                color: userColors.primary 
              }}
            >
              <Phone className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Call</span>
            </button>
            <button 
              onClick={handleEmail}
              className="flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all"
              style={{ 
                borderColor: `${userColors.primary}30`,
                color: userColors.primary 
              }}
            >
              <Mail className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Email</span>
            </button>
            <button 
              onClick={handleWebsite}
              className="flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all"
              style={{ 
                borderColor: `${userColors.primary}30`,
                color: userColors.primary 
              }}
            >
              <Globe className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Website</span>
            </button>
          </div>
          
          <div className="grid grid-cols-3 gap-2">
            <button 
              onClick={handleQR}
              className="flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all"
              style={{ 
                borderColor: `${userColors.secondary}30`,
                color: userColors.secondary 
              }}
            >
              <QrCode className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">QR</span>
            </button>
            <button 
              onClick={handleShare}
              className="flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all"
              style={{ 
                borderColor: `${userColors.secondary}30`,
                color: userColors.secondary 
              }}
            >
              <Share2 className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Share</span>
            </button>
            <button 
              onClick={handleBroadcast}
              className={`flex items-center justify-center p-3 rounded-lg border hover:shadow-sm transition-all cursor-pointer`}
              style={{ 
                borderColor: `${userColors.secondary}30`,
                color: userColors.secondary
              }}
            >
              <Radio className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Cast</span>
            </button>
          </div>
        </div>

        {/* Section 3: Bio Info */}
        <div className="mb-6">
          <h3 
            className="text-sm font-semibold mb-2 uppercase tracking-wide"
            style={{ color: userColors.text }}
          >
            About
          </h3>
          <div 
            className="p-3 rounded-lg text-sm leading-relaxed"
            style={{ 
              backgroundColor: userColors.background,
              color: userColors.text
            }}
          >
            {user.bio}
          </div>
        </div>

        {/* Section 4: Contact & Social */}
        <div className="mb-6">
          <h3 
            className="text-sm font-semibold mb-3 uppercase tracking-wide"
            style={{ color: userColors.text }}
          >
            Contact
          </h3>
          <div className="space-y-2 mb-4">
            <button 
              onClick={handleCall}
              className="w-full flex items-center justify-between p-2 rounded transition-colors"
              style={{ 
                backgroundColor: userColors.background,
                color: userColors.text
              }}
            >
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3" style={{ color: userColors.primary }} />
                <span className="text-sm">{user.phone}</span>
              </div>
              <ArrowRight className="w-4 h-4" style={{ color: userColors.secondary }} />
            </button>
            <button 
              onClick={handleEmail}
              className="w-full flex items-center justify-between p-2 rounded transition-colors"
              style={{ 
                backgroundColor: userColors.background,
                color: userColors.text
              }}
            >
              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-3" style={{ color: userColors.primary }} />
                <span className="text-sm">{user.email}</span>
              </div>
              <ArrowRight className="w-4 h-4" style={{ color: userColors.secondary }} />
            </button>
            <button 
              onClick={handleWebsite}
              className="w-full flex items-center justify-between p-2 rounded transition-colors"
              style={{ 
                backgroundColor: userColors.background,
                color: userColors.text
              }}
            >
              <div className="flex items-center">
                <Globe className="w-4 h-4 mr-3" style={{ color: userColors.primary }} />
                <span className="text-sm">{user.website}</span>
              </div>
              <ArrowRight className="w-4 h-4" style={{ color: userColors.secondary }} />
            </button>
          </div>

          <h4 
            className="text-sm font-semibold mb-2 uppercase tracking-wide"
            style={{ color: userColors.text }}
          >
            Social
          </h4>
          <div className="flex space-x-3">
            {(() => {
              const PRIORITY = ['facebook','instagram','tiktok','twitter','linkedin'];
              const normalize = (s: string) => String(s || '').trim().toLowerCase();
              const alias = (p: string, url?: string) => {
                const k = normalize(p);
                const u = normalize(url || '');
                if (k === 'x' || k.includes('x.com') || u.includes('x.com')) return 'twitter';
                if (k.includes('twitter') || u.includes('twitter.com')) return 'twitter';
                return k;
              };
              const ALLOWED = new Set(['facebook','instagram','tiktok','twitter','linkedin']);
              const raw = Object.entries((user as any).socialLinks || (user as any).social || {})
                .filter(([_, url]) => url && String(url).trim() !== '');
              const entries = raw
                .map(([p, u]) => [alias(String(p), String(u)), u] as [string, any])
                .filter(([p]) => ALLOWED.has(String(p)));
              entries.sort((a, b) => PRIORITY.indexOf(String(a[0])) - PRIORITY.indexOf(String(b[0])));
              return entries.slice(0, 3).map(([platform, url]) => {
                return (
                  <a 
                    key={String(platform)}
                    href={String(url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => handleSocialClick(String(platform))}
                    className="p-2 rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="social" size={20} title={String(platform)} className="" color={userColors.accent} />
                  </a>
                );
              });
            })()}
          </div>
          {Object.keys((user as any).streamingLinks || {}).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries((user as any).streamingLinks || {})
                .filter(([platform, url]) => {
                  const p = String(platform).toLowerCase();
                  const allowed = new Set(['spotify','youtube','soundcloud','boomplay','deezer','tidal','audiomack']);
                  return url && String(url).trim() !== '' && allowed.has(p);
                })
                .map(([platform, url]) => (
                  <a
                    key={platform}
                    href={String(url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-gray-100 text-gray-700"
                    onClick={() => onAnalyticsUpdate?.(user.id, 'socialClicks')}
                  >
                    <UnifiedBrandIcon platform={String(platform)} kind="streaming" size={12} title={String(platform)} className="" color={userColors.accent} />
                    {platform}
                  </a>
                ))}
            </div>
          )}
        </div>

        {/* Contact Exchange Button */}
        {onContactExchange && !isOwnerView && (
          <button
            onClick={onContactExchange}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium text-white mb-3 hover:opacity-90 transition-opacity"
            style={{ backgroundColor: userColors.primary }}
          >
            <UserPlus className="w-4 h-4" />
            Send {user.name?.split(' ')[0] || 'them'} your contact
          </button>
        )}

        {/* Section 5: Save Card Button */}
        <button 
          onClick={handleSaveCard}
          className="w-full flex items-center justify-center py-3 rounded-lg font-medium text-white mb-6 hover:opacity-90 transition-opacity"
          style={{ backgroundColor: userColors.saveButton }}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M5 20h14" />
          </svg>
          Save Contact
        </button>

        {/* Section 6: Ads Space */}
        {currentAd && (
          <div className="border-t pt-4 mb-4">
            <div className="text-center relative overflow-hidden">
              <p className="text-xs text-gray-400 mb-2 uppercase tracking-wide">Sponsored</p>
              <div className="relative">
                <a 
                  href={currentAdHref} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block transition-all duration-500 ease-in-out"
                  key={currentAdIndex}
                  onClick={() => { try { const id = (currentAd as any)?._id || (currentAd as any)?.id || (currentAd as any)?.adId; if (id) { try { trackAdClick(String(id)).catch(() => {}); } catch {} } } catch {} }}
                >
                  <div className="border rounded-lg p-3 hover:shadow-sm transition-all duration-500" style={{ height: 150 }}>
                    <img 
                      src={currentAd.image} 
                      alt={currentAd.title}
                      className="w-full h-16 object-cover rounded mb-2 transition-opacity duration-500"
                    />
                    <h4 className="text-sm font-semibold text-gray-900 mb-1 transition-opacity duration-500 truncate" title={currentAd.title}>{currentAd.title}</h4>
                    <p className="text-xs text-gray-600 transition-opacity duration-500 line-clamp-2" title={currentAd.description}>{currentAd.description}</p>
                  </div>
                </a>
                
                {/* Ad indicators */}
                {activeAds.length > 1 && (
                  <div className="flex justify-center mt-2 space-x-1">
                    {activeAds.map((_, index) => (
                      <div
                        key={index}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                          index === currentAdIndex ? 'bg-gray-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>

      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        user={user}
      />

      <EnhancedEmailLeadForm
        isOpen={showEmailForm}
        onClose={() => setShowEmailForm(false)}
        recipientName={user.name}
        recipientEmail={user.email}
        cardOwnerId={user.id}
        onLeadSubmit={onLeadSubmit}
        onAnalyticsUpdate={() => onAnalyticsUpdate?.(user.id, 'formSubmissions')}
      />

      <BroadcastModal
        isOpen={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        userName={user.name}
        cardOwnerId={user.id}
        leads={(authedLeads ?? leads) as any}
      />
      <BroadcastLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={async ({ id }) => {
          const uid = String(user.id);
          if (String(id) !== uid) { safeToast('error', 'This account does not own this card.'); return; }
          const exp = user.subscription?.expiresAt ? new Date(user.subscription.expiresAt as any) : null;
          const active = (user.subscription?.isActive ?? true) && (!exp || exp > new Date());
          if (!active || !(['gold','platinum'].includes(String(user.subscription?.type)))) { safeToast('warning', 'Broadcast requires an active Gold or Platinum subscription.'); return; }
          try { const fresh = await listLeadsByOwner(String(uid)).catch(() => null); if (fresh && Array.isArray(fresh)) setAuthedLeads(fresh as any); } catch {}
          setShowBroadcastModal(true);
          try { onAnalyticsUpdate?.(user.id, 'messageClicks'); } catch {}
        }}
      />
  {/* share popup handled by global ShareProvider */}
    </>
  );
};